// this file is copied from backend
// https://github.com/Lyearn/LyearnModules_v2/blob/b468164739e8f051909c6675f77e989c8bd71be0/packages/lyearn-classroom-rpc-contract/src/utils/cron/buildRecurrenceRuleFromSchedule.util.ts
// not using directly from backend due to internal dependencies on rpc-contract

import isEmpty from 'lodash/isEmpty';
import { Frequency, RRule, Weekday } from 'rrule';

import {
  CronFrequencyEnum,
  CustomFrequencyEnum,
  DayOfWeekEnum,
  Maybe,
  MonthOfYearEnum,
  ScheduleInfo,
  WeekOfMonthEnum,
} from '@/types/schema';

import { GetNextOccurrenceDateFromScheduleInput } from './types';

const dayOfWeekToRRuleWeekdayMapping = {
  [DayOfWeekEnum.Monday]: RRule.MO,
  [DayOfWeekEnum.Tuesday]: RRule.TU,
  [DayOfWeekEnum.Wednesday]: RRule.WE,
  [DayOfWeekEnum.Thursday]: RRule.TH,
  [DayOfWeekEnum.Friday]: RRule.FR,
  [DayOfWeekEnum.Saturday]: RRule.SA,
  [DayOfWeekEnum.Sunday]: RRule.SU,
};

const weekOfMonthToWeekNumberMapping = {
  [WeekOfMonthEnum.First]: 1,
  [WeekOfMonthEnum.Second]: 2,
  [WeekOfMonthEnum.Third]: 3,
  [WeekOfMonthEnum.Fourth]: 4,
  [WeekOfMonthEnum.Last]: -1,
};

const customFrequencyToRRuleFrequencyMapping = {
  [CustomFrequencyEnum.Day]: RRule.DAILY,
  [CustomFrequencyEnum.Month]: RRule.MONTHLY,
  [CustomFrequencyEnum.Week]: RRule.WEEKLY,
  [CustomFrequencyEnum.Year]: RRule.YEARLY,
  [CustomFrequencyEnum.Minute]: RRule.MINUTELY,
};

function getRecurrenceRuleDayOfWeek(dayOfWeek: DayOfWeekEnum): Weekday {
  return dayOfWeekToRRuleWeekdayMapping[dayOfWeek];
}

function getRecurrenceRuleFrequency(frequency: CustomFrequencyEnum): Frequency {
  return customFrequencyToRRuleFrequencyMapping[frequency];
}

/**
 * Provides RRule that will select particular daysOfWeek for given weeksOfMonth.
 *
 * @returns weekdays in the following format - [RRule.MO.nth(1), RRule.FR.nth(4), ...]
 */
function getRuleForDaysOfNthWeeks(
  daysOfWeek: DayOfWeekEnum[],
  weeksOfMonth: WeekOfMonthEnum[],
): Weekday[] {
  const weekdays: Weekday[] = [];
  for (const dayOfWeek of daysOfWeek) {
    const weekday = getRecurrenceRuleDayOfWeek(dayOfWeek);
    for (const weekOfMonth of weeksOfMonth) {
      const weekNumber = weekOfMonthToWeekNumberMapping[weekOfMonth];
      weekdays.push(weekday.nth(weekNumber));
    }
  }

  return weekdays;
}

export function getUTCDate(date: Date): Date {
  return new Date(
    Date.UTC(
      date.getFullYear(),
      date.getMonth(),
      date.getDate(),
      date.getHours(),
      date.getMinutes(),
    ),
  );
}

const getNumberedMonthOfYear = (monthsOfYear?: Maybe<MonthOfYearEnum[]>) => {
  return monthsOfYear?.map((monthOfYear) => {
    switch (monthOfYear) {
      case MonthOfYearEnum.January: {
        return 1;
      }
      case MonthOfYearEnum.February: {
        return 2;
      }
      case MonthOfYearEnum.March: {
        return 3;
      }
      case MonthOfYearEnum.April: {
        return 4;
      }
      case MonthOfYearEnum.May: {
        return 5;
      }
      case MonthOfYearEnum.June: {
        return 6;
      }
      case MonthOfYearEnum.July: {
        return 7;
      }
      case MonthOfYearEnum.August: {
        return 8;
      }
      case MonthOfYearEnum.September: {
        return 9;
      }
      case MonthOfYearEnum.October: {
        return 10;
      }
      case MonthOfYearEnum.November: {
        return 11;
      }
      case MonthOfYearEnum.December: {
        return 12;
      }
    }
  });
};

export function mergeArray<T>(array?: Maybe<T[]>, element?: Maybe<T>): T[] | undefined {
  if (!Array.isArray(array) || array.length === 0) {
    return element ? [element] : undefined;
  }
  if (element) {
    return [...array, element];
  }
  return array;
}

export function mergeCronSchedule(schedule: ScheduleInfo): ScheduleInfo {
  const {
    dayOfWeek,
    weekOfMonth,
    MonthOfYear,
    custom,
    daysOfWeek: inputDaysOfWeek,
    weeksOfMonth: inputWeeksOfMonth,
    monthsOfYear: inputMonthsOfYear,
  } = schedule;

  const daysOfWeek = mergeArray(inputDaysOfWeek, dayOfWeek);
  const weeksOfMonth = mergeArray(inputWeeksOfMonth, weekOfMonth);
  const monthsOfYear = mergeArray(inputMonthsOfYear, MonthOfYear);

  let customWeeksOfMonthArray: WeekOfMonthEnum[] | undefined;
  let customMonthsOfYearArray: MonthOfYearEnum[] | undefined;
  if (custom) {
    const {
      weekOfMonth: customWeekOfMonth,
      monthOfYear: customMonthOfYear,
      weeksOfMonth: customWeeksOfMonth,
      monthsOfYear: customMonthsOfYear,
    } = custom;
    customWeeksOfMonthArray = mergeArray(customWeeksOfMonth, customWeekOfMonth);
    customMonthsOfYearArray = mergeArray(customMonthsOfYear, customMonthOfYear);
  }

  return {
    ...schedule,
    daysOfWeek,
    weeksOfMonth,
    monthsOfYear,
    custom: custom
      ? { ...custom, weeksOfMonth: customWeeksOfMonthArray, monthsOfYear: customMonthsOfYearArray }
      : undefined,
  };
}

function buildCustomRecurrenceRule(schedule: ScheduleInfo, count?: number): RRule {
  const { custom, time, timezone, startDate } = schedule;
  const [hour, minute] = time.split(':');
  const byhour = Number(hour);
  const byminute = Number(minute);
  const dtstart = startDate ? getUTCDate(new Date(startDate)) : getUTCDate(new Date());
  const {
    daysOfWeek: inputDaysOfWeek,
    weeksOfMonth: inputWeeksOfMonth,
    frequency,
    interval,
    dayOfMonth: customDayOfMonth,
    monthsOfYear: customMonthsOfYear,
  } = custom!;
  const daysOfWeek = inputDaysOfWeek ? inputDaysOfWeek.map(getRecurrenceRuleDayOfWeek) : undefined;
  switch (frequency) {
    case CustomFrequencyEnum.Day: {
      return new RRule({
        freq: getRecurrenceRuleFrequency(frequency),
        interval,
        byhour,
        byminute,
        tzid: timezone,
        dtstart,
        count,
      });
    }
    case CustomFrequencyEnum.Week: {
      return new RRule({
        freq: getRecurrenceRuleFrequency(frequency),
        byhour,
        byminute,
        byweekday: daysOfWeek,
        tzid: timezone,
        dtstart,
        interval,
        count,
      });
    }
    case CustomFrequencyEnum.Month: {
      let byWeekDay: Weekday[] | undefined;
      if (!inputWeeksOfMonth || isEmpty(inputWeeksOfMonth)) {
        byWeekDay = daysOfWeek;
      } else if (inputDaysOfWeek) {
        byWeekDay = getRuleForDaysOfNthWeeks(inputDaysOfWeek, inputWeeksOfMonth);
      }

      return new RRule({
        freq: getRecurrenceRuleFrequency(frequency),
        byhour,
        byminute,
        byweekday: byWeekDay,
        tzid: timezone,
        dtstart,
        interval,
        count,
      });
    }
    case CustomFrequencyEnum.Year: {
      return new RRule({
        freq: getRecurrenceRuleFrequency(frequency),
        byhour,
        byminute,
        byweekday: daysOfWeek,
        bymonth: getNumberedMonthOfYear(customMonthsOfYear),
        bymonthday: customDayOfMonth,
        tzid: timezone,
        dtstart,
        interval,
        count,
      });
    }
    case CustomFrequencyEnum.Minute: {
      return new RRule({
        freq: getRecurrenceRuleFrequency(frequency),
        byhour,
        byminute,
        byweekday: daysOfWeek,
        bymonth: getNumberedMonthOfYear(customMonthsOfYear),
        bymonthday: customDayOfMonth,
        tzid: timezone,
        dtstart,
        interval,
        count,
      });
    }
  }
}

export function buildRecurrenceRuleFromSchedule(
  params: GetNextOccurrenceDateFromScheduleInput,
): RRule {
  const { schedule, options, count } = params;
  // validateCronSchedule(schedule);
  const mergedSchedule = mergeCronSchedule(schedule);
  const {
    time,
    frequency,
    timezone,
    dayOfMonth,
    custom,
    daysOfWeek: inputDaysOfWeek,
    weeksOfMonth: inputWeeksOfMonth,
    monthsOfYear: inputMonthsOfYear,
  } = mergedSchedule;
  const startDate = options?.startDate ?? mergedSchedule.startDate;
  const [hour, minute] = time.split(':');
  const byhour = Number(hour);
  const byminute = Number(minute);
  const daysOfWeek = inputDaysOfWeek ? inputDaysOfWeek.map(getRecurrenceRuleDayOfWeek) : undefined;
  const dtstart = startDate ? getUTCDate(new Date(startDate)) : getUTCDate(new Date());

  switch (frequency) {
    case CronFrequencyEnum.Daily: {
      return new RRule({
        freq: RRule.DAILY,
        byhour,
        byminute,
        tzid: timezone,
        dtstart,
        count,
      });
    }
    case CronFrequencyEnum.Weekly: {
      return new RRule({
        freq: RRule.WEEKLY,
        byhour,
        byminute,
        byweekday: daysOfWeek,
        tzid: timezone,
        dtstart,
        count,
      });
    }
    case CronFrequencyEnum.Fortnightly: {
      return new RRule({
        freq: RRule.WEEKLY,
        interval: 2,
        byhour,
        byminute,
        byweekday: daysOfWeek,
        tzid: timezone,
        dtstart,
        count,
      });
    }
    case CronFrequencyEnum.Monthly: {
      let byWeekDay: Weekday[] | undefined;
      if (!inputWeeksOfMonth || isEmpty(inputWeeksOfMonth)) {
        byWeekDay = daysOfWeek;
      } else if (inputDaysOfWeek) {
        byWeekDay = getRuleForDaysOfNthWeeks(inputDaysOfWeek, inputWeeksOfMonth);
      }
      return new RRule({
        freq: RRule.MONTHLY,
        byhour,
        byminute,
        byweekday: byWeekDay,
        tzid: timezone,
        dtstart,
        count,
      });
    }
    case CronFrequencyEnum.Quarterly: {
      let byWeekDay: Weekday[] | undefined;
      if (!inputWeeksOfMonth || isEmpty(inputWeeksOfMonth)) {
        byWeekDay = daysOfWeek;
      } else if (inputDaysOfWeek) {
        byWeekDay = getRuleForDaysOfNthWeeks(inputDaysOfWeek, inputWeeksOfMonth);
      }
      return new RRule({
        freq: RRule.MONTHLY,
        interval: 3,
        byhour,
        byminute,
        byweekday: byWeekDay,
        tzid: timezone,
        dtstart,
        count,
      });
    }

    case CronFrequencyEnum.Yearly: {
      return new RRule({
        freq: RRule.YEARLY,
        byhour,
        byminute,
        byweekday: daysOfWeek,
        bymonth: getNumberedMonthOfYear(inputMonthsOfYear),
        bymonthday: dayOfMonth,
        tzid: timezone,
        dtstart,
        count,
      });
    }
    case CronFrequencyEnum.Custom: {
      if (!custom) {
        throw new TypeError(`buildRecurrenceRuleFromSchedule: custom frequency missing`);
      }
      return buildCustomRecurrenceRule(schedule, count);
    }
    default: {
      throw new TypeError(`buildRecurrenceRuleFromSchedule: Invalid frequency`);
    }
  }
}
